import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import multiavatar from '@multiavatar/multiavatar/esm';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPreferences, setUserPreferences] = useState(() => {
    const saved = localStorage.getItem('avatarPreferences');
    return saved ? JSON.parse(saved) : {};
  });

  // Configure axios base URL
  axios.defaults.baseURL = 'https://api.silkspace.co/api';
  axios.defaults.withCredentials = false;

  // Cache for generated avatars
  const avatarCache = new Map();

  /**
   * Default avatar themes
   * A = Light theme
   * B = Colorful theme (default)
   * C = Dark theme
   */
  const AVAILABLE_THEMES = {
    A: { id: 'A', name: 'Light Theme', description: 'Light color palette' },
    B: { id: 'B', name: 'Colorful Theme', description: 'Vibrant, diverse colors' },
    C: { id: 'C', name: 'Dark Theme', description: 'Rich, deeper colors' }
  };

  /**
   * Get user's preferred avatar settings
   */
  const getAvatarSettings = (user) => {
    if (!user) return { theme: 'B', version: '00' };
    
    const userId = user.id || user.username;
    
    // Check if user has saved preferences
    if (userId && userPreferences[userId]) {
      return userPreferences[userId];
    }
    
    // Default to colorful theme
    return { theme: 'B', version: '00' };
  };

  /**
   * Save user's avatar preferences
   */
  const saveAvatarPreferences = (userId, preferences) => {
    const newPreferences = {
      ...userPreferences,
      [userId]: preferences
    };
    setUserPreferences(newPreferences);
    localStorage.setItem('avatarPreferences', JSON.stringify(newPreferences));
  };

  /**
   * Generate Multiavatar with custom theme
   */
  const generateMultiavatarSVG = (identifier, settings = { theme: 'B', version: '00' }) => {
    const cacheKey = `avatar_${identifier}_${settings.theme}_${settings.version}`;
    
    if (avatarCache.has(cacheKey)) {
      return avatarCache.get(cacheKey);
    }

    try {
      const cleanIdentifier = identifier.trim() || 'User';
      
      // Generate avatar with specific theme
      const svgCode = multiavatar(cleanIdentifier, settings);
      
      const svgBlob = new Blob([svgCode], { type: 'image/svg+xml;charset=utf-8' });
      const dataUrl = URL.createObjectURL(svgBlob);
      
      avatarCache.set(cacheKey, dataUrl);
      return dataUrl;
    } catch (error) {
      console.error('Error generating Multiavatar:', error);
      
      // Fallback avatar with theme color
      const themeColor = settings.theme === 'C' ? '#8B5CF6' : 
                        settings.theme === 'A' ? '#3B82F6' : '#ff9702';
      
      const fallbackSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100">
        <circle cx="50" cy="35" r="20" fill="${themeColor}"/>
        <circle cx="50" cy="35" r="15" fill="#ffffff"/>
        <circle cx="43" cy="32" r="3" fill="#1d1d1d"/>
        <circle cx="57" cy="32" r="3" fill="#1d1d1d"/>
        <path d="M45,40 Q50,45 55,40" stroke="#1d1d1d" stroke-width="2" fill="none"/>
        <ellipse cx="50" cy="75" rx="25" ry="30" fill="${themeColor}"/>
      </svg>`;
      
      const fallbackBlob = new Blob([fallbackSvg], { type: 'image/svg+xml;charset=utf-8' });
      const fallbackUrl = URL.createObjectURL(fallbackBlob);
      
      avatarCache.set(cacheKey, fallbackUrl);
      return fallbackUrl;
    }
  };

  /**
   * Generate fallback avatar with colored background and initial
   */
  const generateFallbackAvatar = (user) => {
    const base = (user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.first_name || user.username || 'User').trim();

    const stringHash = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return hash;
    };

    const colors = [
      '#ff9702', '#1d1d1d', '#3B82F6', '#EF4444', '#10B981', 
      '#8B5CF6', '#F59E0B', '#EC4899', '#06B6D4', '#84CC16'
    ];
    
    const hash = Math.abs(stringHash(base));
    const colorIndex = hash % colors.length;
    const color = colors[colorIndex];
    
    return {
      background: color,
      letter: (base.charAt(0) || 'U').toUpperCase()
    };
  };

  /**
   * Generate permanent avatar URL for a user
   */
  const generatePermanentAvatar = (user) => {
    if (!user) {
      return generateMultiavatarSVG('anonymous', { theme: 'B', version: '00' });
    }
    
    const identifier = user.username || user.email || user.id || 'user';
    const settings = getAvatarSettings(user);
    
    return generateMultiavatarSVG(identifier, settings);
  };

  /**
   * Get user avatar with theme awareness
   */
  const getUserAvatar = (user) => {
    if (!user) {
      return {
        url: generateMultiavatarSVG('anonymous', { theme: 'B', version: '00' }),
        fallback: {
          background: '#cccccc',
          letter: '?'
        }
      };
    }
    
    const settings = getAvatarSettings(user);
    const avatarUrl = generatePermanentAvatar(user);
    
    return {
      url: avatarUrl,
      fallback: generateFallbackAvatar(user)
    };
  };

  /**
   * Update user's avatar preferences
   */
  const updateAvatarPreferences = (preferences) => {
    if (!currentUser) return false;
    
    const userId = currentUser.id || currentUser.username;
    saveAvatarPreferences(userId, preferences);
    
    // Clear avatar cache for this user to regenerate with new settings
    const cacheKeys = Array.from(avatarCache.keys()).filter(key => 
      key.includes(userId) || key.includes(currentUser.username)
    );
    cacheKeys.forEach(key => avatarCache.delete(key));
    
    return true;
  };

  /**
   * Get available avatar themes for UI
   */
  const getAvailableThemes = () => {
    return [
      { id: 'B', name: 'Colorful Avatar', description: 'Vibrant and colorful appearance' },
      { id: 'A', name: 'Light Avatar', description: 'Lighter color palette' },
      { id: 'C', name: 'Dark Avatar', description: 'Darker, rich colors' }
    ];
  };

  /**
   * Clean up object URLs when component unmounts
   */
  useEffect(() => {
    return () => {
      avatarCache.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      avatarCache.clear();
    };
  }, []);

  /**
   * Authentication functions
   */
  const verifyToken = async () => {
    try {
      await axios.get('/users/me/');
      setLoading(false);
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post('/users/login/', {
        username,
        password
      });
      
      const user = response.data;
      setCurrentUser(user);
      localStorage.setItem('token', user.token);
      localStorage.setItem('userData', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Token ${user.token}`;
      
      return { success: true, user };
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Login failed';
      
      if (error.response) {
        if (error.response.status === 400) {
          errorMessage = error.response.data.non_field_errors?.[0] || 'Invalid credentials';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied. Please try again.';
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const register = async (formData) => {
    try {
      const response = await axios.post('/users/register/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const user = response.data;
      setCurrentUser(user);
      localStorage.setItem('token', user.token);
      localStorage.setItem('userData', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Token ${user.token}`;
      
      // Save avatar preferences if provided
      const avatarPreferences = formData.get('avatar_preferences');
      if (avatarPreferences) {
        try {
          const preferences = JSON.parse(avatarPreferences);
          const userId = user.id || user.username;
          if (userId) {
            saveAvatarPreferences(userId, preferences);
          }
        } catch (e) {
          console.log('No avatar preferences provided or invalid format');
        }
      }
      
      return { success: true, user };
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'Registration failed';
      
      if (error.response) {
        if (error.response.status === 400) {
          const errors = error.response.data;
          if (typeof errors === 'object') {
            const firstError = Object.values(errors)[0];
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          }
        } else if (error.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection.';
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/users/logout/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setCurrentUser(null);
      localStorage.removeItem('token');
      localStorage.removeItem('userData');
      delete axios.defaults.headers.common['Authorization'];
      avatarCache.clear();
    }
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await axios.post('/users/change_password/', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      
      if (response.data.user && response.data.user.token) {
        localStorage.setItem('token', response.data.user.token);
        localStorage.setItem('userData', JSON.stringify(response.data.user));
        setCurrentUser(response.data.user);
      }
      
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Change password error:', error);
      let errorMessage = 'Failed to change password';
      
      if (error.response) {
        if (error.response.status === 400) {
          const errors = error.response.data;
          if (typeof errors === 'object') {
            const firstError = Object.values(errors)[0];
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          }
        }
      }
      
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Initialize auth state from localStorage
   */
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('userData');
    
    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        setCurrentUser(user);
        axios.defaults.headers.common['Authorization'] = `Token ${token}`;
        verifyToken();
      } catch (error) {
        console.error('Error parsing user data:', error);
        logout();
      }
    } else {
      setLoading(false);
    }
  }, []);

  const value = {
    currentUser,
    login,
    register,
    logout,
    loading,
    getUserAvatar,
    changePassword,
    updateAvatarPreferences,
    getAvailableThemes,
    clearAvatarCache: () => avatarCache.clear()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};